import type { AssetId } from '@shapeshiftoss/caip'
import * as caip from '@shapeshiftoss/caip'
import type { Asset } from '@shapeshiftoss/types'

const mayaTokenAssetId: AssetId = 'cosmos:mayachain-mainnet-v1/slip44:maya'

export const ethereum: Readonly<Asset> = Object.freeze({
  assetId: caip.ethAssetId,
  chainId: caip.ethChainId,
  symbol: 'ETH',
  name: 'Ethereum',
  networkName: 'Ethereum',
  precision: 18,
  color: '#5C6BC0',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/32e51d582a890b3dd3135fe3ee7c20c2fd699a6d/blockchains/ethereum/info/logo.png',
  explorer: 'https://etherscan.io',
  explorerAddressLink: 'https://etherscan.io/address/',
  explorerTxLink: 'https://etherscan.io/tx/',
  relatedAssetKey: 'eip155:1/slip44:60',
})

export const bitcoin: Readonly<Asset> = Object.freeze({
  assetId: caip.btcAssetId,
  chainId: caip.btcChainId,
  symbol: 'BTC',
  name: 'Bitcoin',
  networkName: 'Bitcoin',
  precision: 8,
  color: '#FF9800',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/b7a5f12d893fcf58e0eb1dd64478f076857b720b/blockchains/bitcoin/info/logo.png',
  explorer: 'https://live.blockcypher.com',
  explorerAddressLink: 'https://live.blockcypher.com/btc/address/',
  explorerTxLink: 'https://live.blockcypher.com/btc/tx/',
  relatedAssetKey: null,
})

export const bitcoincash: Readonly<Asset> = Object.freeze({
  assetId: caip.bchAssetId,
  chainId: caip.bchChainId,
  symbol: 'BCH',
  name: 'Bitcoin Cash',
  networkName: 'Bitcoin Cash',
  precision: 8,
  color: '#8BC34A',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/b7a5f12d893fcf58e0eb1dd64478f076857b720b/blockchains/bitcoincash/info/logo.png',
  explorer: 'https://blockchair.com',
  explorerAddressLink: 'https://blockchair.com/bitcoin-cash/address/',
  explorerTxLink: 'https://blockchair.com/bitcoin-cash/transaction/',
  relatedAssetKey: null,
})

export const dogecoin: Readonly<Asset> = Object.freeze({
  assetId: caip.dogeAssetId,
  chainId: caip.dogeChainId,
  symbol: 'DOGE',
  name: 'Dogecoin',
  networkName: 'Dogecoin',
  precision: 8,
  color: '#FFC107',
  icon: 'https://assets.coingecko.com/coins/images/5/large/dogecoin.png?1696501409',
  explorer: 'https://live.blockcypher.com',
  explorerAddressLink: 'https://live.blockcypher.com/doge/address/',
  explorerTxLink: 'https://live.blockcypher.com/doge/tx/',
  relatedAssetKey: null,
})

export const litecoin: Readonly<Asset> = Object.freeze({
  assetId: caip.ltcAssetId,
  chainId: caip.ltcChainId,
  symbol: 'LTC',
  name: 'Litecoin',
  networkName: 'Litecoin',
  precision: 8,
  color: '#B8B8B8',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/32e51d582a890b3dd3135fe3ee7c20c2fd699a6d/blockchains/litecoin/info/logo.png',
  explorer: 'https://live.blockcypher.com',
  explorerAddressLink: 'https://live.blockcypher.com/ltc/address/',
  explorerTxLink: 'https://live.blockcypher.com/ltc/tx/',
  relatedAssetKey: null,
})

export const zcash: Readonly<Asset> = Object.freeze({
  assetId: caip.zecAssetId,
  chainId: caip.zecChainId,
  name: 'Zcash',
  networkName: 'Zcash',
  symbol: 'ZEC',
  precision: 8,
  color: '#f4b728',
  networkColor: '#f4b728',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/fb59e296f64fe3df5afdf8148e88603851b613ae/blockchains/zcash/info/logo.png',
  explorer: 'https://blockchair.com',
  explorerAddressLink: 'https://blockchair.com/zcash/address/',
  explorerTxLink: 'https://blockchair.com/zcash/transaction/',
  relatedAssetKey: null,
})

export const atom: Readonly<Asset> = Object.freeze({
  assetId: caip.cosmosAssetId,
  chainId: caip.cosmosChainId,
  symbol: 'ATOM',
  name: 'Cosmos',
  networkName: 'Cosmos',
  precision: 6,
  color: '#303F9F',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/b7a5f12d893fcf58e0eb1dd64478f076857b720b/blockchains/cosmos/info/logo.png',
  explorer: 'https://www.mintscan.io/cosmos',
  explorerAddressLink: 'https://www.mintscan.io/cosmos/account/',
  explorerTxLink: 'https://www.mintscan.io/cosmos/txs/',
  relatedAssetKey: null,
})

export const avax: Readonly<Asset> = Object.freeze({
  assetId: caip.avalancheAssetId,
  chainId: caip.avalancheChainId,
  name: 'Avalanche',
  networkName: 'Avalanche C-Chain',
  symbol: 'AVAX',
  precision: 18,
  color: '#FFFFFF', // this will get picked up by the color generation script,
  icon: 'https://rawcdn.githack.com/trustwallet/assets/b7a5f12d893fcf58e0eb1dd64478f076857b720b/blockchains/avalanchec/info/logo.png',
  explorer: 'https://snowtrace.dev',
  explorerAddressLink: 'https://snowtrace.dev/address/',
  explorerTxLink: 'https://snowtrace.dev/tx/',
  relatedAssetKey: null,
})

export const thorchain: Readonly<Asset> = Object.freeze({
  assetId: caip.thorchainAssetId,
  chainId: caip.thorchainChainId,
  name: 'THORChain',
  networkName: 'THORChain',
  symbol: 'RUNE',
  precision: 8,
  color: '#33FF99',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/32e51d582a890b3dd3135fe3ee7c20c2fd699a6d/blockchains/thorchain/info/logo.png',
  explorer: 'https://viewblock.io/thorchain',
  explorerAddressLink: 'https://viewblock.io/thorchain/address/',
  explorerTxLink: 'https://viewblock.io/thorchain/tx/',
  relatedAssetKey: null,
})

export const tcy: Readonly<Asset> = Object.freeze({
  assetId: caip.tcyAssetId,
  chainId: caip.thorchainChainId,
  name: 'TCY',
  networkName: 'THORChain',
  symbol: 'TCY',
  precision: 8,
  color: '#33FF99',
  icon: 'https://raw.githubusercontent.com/shapeshift/web/develop/src/assets/tcy-icon.png',
  explorer: 'https://viewblock.io/thorchain',
  explorerAddressLink: 'https://viewblock.io/thorchain/address/',
  explorerTxLink: 'https://viewblock.io/thorchain/tx/',
  relatedAssetKey: null,
})

export const ruji: Readonly<Asset> = Object.freeze({
  assetId: caip.rujiAssetId,
  chainId: caip.thorchainChainId,
  name: 'Rujira',
  networkName: 'THORChain',
  symbol: 'RUJI',
  precision: 8,
  color: '#D800D8',
  icon: 'https://raw.githubusercontent.com/shapeshift/web/develop/src/assets/ruji-icon.png',
  explorer: 'https://viewblock.io/thorchain',
  explorerAddressLink: 'https://viewblock.io/thorchain/address/',
  explorerTxLink: 'https://viewblock.io/thorchain/tx/',
  relatedAssetKey: null,
})

export const maya: Readonly<Asset> = Object.freeze({
  assetId: mayaTokenAssetId,
  chainId: caip.mayachainChainId,
  name: 'MAYA',
  networkName: 'MAYAChain',
  symbol: 'MAYA',
  precision: 4,
  color: '#00FFA0',
  icon: 'https://raw.githubusercontent.com/shapeshift/web/develop/src/assets/maya.png',
  explorer: 'https://mayascan.org',
  explorerAddressLink: 'https://mayascan.org/address/',
  explorerTxLink: 'https://mayascan.org/tx/',
  relatedAssetKey: null,
})

export const mayachain: Readonly<Asset> = Object.freeze({
  assetId: caip.mayachainAssetId,
  chainId: caip.mayachainChainId,
  name: 'MAYAChain',
  networkName: 'MAYAChain',
  symbol: 'CACAO',
  precision: 10,
  color: '#63FDD9',
  icon: 'https://raw.githubusercontent.com/shapeshift/web/develop/src/assets/cacao.png',
  networkIcon: 'https://raw.githubusercontent.com/shapeshift/web/develop/src/assets/mayachain.png',
  explorer: 'https://mayascan.org',
  explorerAddressLink: 'https://mayascan.org/address/',
  explorerTxLink: 'https://mayascan.org/tx/',
  relatedAssetKey: null,
})

export const optimism: Readonly<Asset> = Object.freeze({
  assetId: caip.optimismAssetId,
  chainId: caip.optimismChainId,
  name: 'Ethereum',
  networkName: 'Optimism',
  symbol: 'ETH',
  precision: 18,
  color: '#5C6BC0',
  networkColor: '#FC0424',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/32e51d582a890b3dd3135fe3ee7c20c2fd699a6d/blockchains/ethereum/info/logo.png',
  networkIcon: 'https://assets.coingecko.com/coins/images/25244/large/Optimism.png?1660904599',
  explorer: 'https://optimistic.etherscan.io',
  explorerAddressLink: 'https://optimistic.etherscan.io/address/',
  explorerTxLink: 'https://optimistic.etherscan.io/tx/',
  relatedAssetKey: 'eip155:1/slip44:60',
})

export const bnbsmartchain: Readonly<Asset> = Object.freeze({
  assetId: caip.bscAssetId,
  chainId: caip.bscChainId,
  name: 'BNB',
  networkName: 'BNB Smart Chain',
  symbol: 'BNB',
  precision: 18,
  color: '#F0B90B',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/b7a5f12d893fcf58e0eb1dd64478f076857b720b/blockchains/binance/info/logo.png',
  explorer: 'https://bscscan.com',
  explorerAddressLink: 'https://bscscan.com/address/',
  explorerTxLink: 'https://bscscan.com/tx/',
  relatedAssetKey: null,
})

export const polygon: Readonly<Asset> = Object.freeze({
  assetId: caip.polygonAssetId,
  chainId: caip.polygonChainId,
  name: 'Polygon Ecosystem Token',
  networkName: 'Polygon',
  symbol: 'POL',
  precision: 18,
  color: '#8f00ff',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/b7a5f12d893fcf58e0eb1dd64478f076857b720b/blockchains/polygon/info/logo.png',
  explorer: 'https://polygonscan.com/',
  explorerAddressLink: 'https://polygonscan.com/address/',
  explorerTxLink: 'https://polygonscan.com/tx/',
  relatedAssetKey: null,
})

export const gnosis: Readonly<Asset> = Object.freeze({
  assetId: caip.gnosisAssetId,
  chainId: caip.gnosisChainId,
  name: 'xDAI',
  networkName: 'Gnosis',
  symbol: 'xDAI',
  precision: 18,
  color: '#33765c',
  icon: 'https://assets.coingecko.com/coins/images/11062/large/Identity-Primary-DarkBG.png?1638372986',
  networkIcon:
    'https://assets.coingecko.com/asset_platforms/images/11062/large/Aatar_green_white.png?1643204471',
  explorer: 'https://gnosis.blockscout.com/',
  explorerAddressLink: 'https://gnosis.blockscout.com/address/',
  explorerTxLink: 'https://gnosis.blockscout.com/tx/',
  relatedAssetKey: null,
})

export const arbitrum: Readonly<Asset> = Object.freeze({
  assetId: caip.arbitrumAssetId,
  chainId: caip.arbitrumChainId,
  name: 'Ethereum',
  networkName: 'Arbitrum One',
  symbol: 'ETH',
  precision: 18,
  color: '#5C6BC0',
  networkColor: '#213147',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/32e51d582a890b3dd3135fe3ee7c20c2fd699a6d/blockchains/ethereum/info/logo.png',
  networkIcon:
    'https://raw.githubusercontent.com/trustwallet/assets/b7a5f12d893fcf58e0eb1dd64478f076857b720b/blockchains/arbitrum/info/logo.png',
  explorer: 'https://arbiscan.io',
  explorerAddressLink: 'https://arbiscan.io/address/',
  explorerTxLink: 'https://arbiscan.io/tx/',
  relatedAssetKey: 'eip155:1/slip44:60',
})

export const arbitrumNova: Readonly<Asset> = Object.freeze({
  assetId: caip.arbitrumNovaAssetId,
  chainId: caip.arbitrumNovaChainId,
  name: 'Ethereum',
  networkName: 'Arbitrum Nova',
  symbol: 'ETH',
  precision: 18,
  color: '#5C6BC0',
  networkColor: '#E67408',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/32e51d582a890b3dd3135fe3ee7c20c2fd699a6d/blockchains/ethereum/info/logo.png',
  networkIcon:
    'https://assets.coingecko.com/asset_platforms/images/93/large/AN_logomark.png?1695026131',
  explorer: 'https://nova.arbiscan.io',
  explorerAddressLink: 'https://nova.arbiscan.io/address/',
  explorerTxLink: 'https://nova.arbiscan.io/tx/',
  relatedAssetKey: 'eip155:1/slip44:60',
})

export const base: Readonly<Asset> = Object.freeze({
  assetId: caip.baseAssetId,
  chainId: caip.baseChainId,
  name: 'Ethereum',
  networkName: 'Base',
  symbol: 'ETH',
  precision: 18,
  color: '#5C6BC0',
  networkColor: '#0052FF',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/32e51d582a890b3dd3135fe3ee7c20c2fd699a6d/blockchains/ethereum/info/logo.png',
  networkIcon:
    'https://rawcdn.githack.com/base-org/brand-kit/8984fe6e08be3058fd7cf5cd0b201f8b92b5a70e/logo/symbol/Base_Symbol_Blue.png',
  explorer: 'https://basescan.org',
  explorerAddressLink: 'https://basescan.org/address/',
  explorerTxLink: 'https://basescan.org/tx/',
  relatedAssetKey: 'eip155:1/slip44:60',
})

export const monad: Readonly<Asset> = Object.freeze({
  assetId: caip.monadAssetId,
  chainId: caip.monadChainId,
  name: 'Monad',
  networkName: 'Monad',
  symbol: 'MON',
  precision: 18,
  color: '#3B82F6',
  networkColor: '#3B82F6',
  icon: 'https://assets.coingecko.com/coins/images/38927/standard/monad.png?1764042736',
  networkIcon: 'https://assets.coingecko.com/coins/images/38927/standard/monad.png?1764042736',
  explorer: 'https://monadvision.com',
  explorerAddressLink: 'https://monadvision.com/address/',
  explorerTxLink: 'https://monadvision.com/tx/',
  relatedAssetKey: null,
})

export const hyperevm: Readonly<Asset> = Object.freeze({
  assetId: caip.hyperEvmAssetId,
  chainId: caip.hyperEvmChainId,
  name: 'HyperEVM',
  networkName: 'HyperEVM',
  symbol: 'HYPE',
  precision: 18,
  color: '#000000',
  networkColor: '#000000',
  icon: 'https://assets.coingecko.com/coins/images/50882/standard/hyperliquid.jpg?1729431300',
  networkIcon:
    'https://assets.coingecko.com/coins/images/50882/standard/hyperliquid.jpg?1729431300',
  explorer: 'https://hyperevmscan.io',
  explorerAddressLink: 'https://hyperevmscan.io/address/',
  explorerTxLink: 'https://hyperevmscan.io/tx/',
  relatedAssetKey: null,
})

export const plasma: Readonly<Asset> = Object.freeze({
  assetId: caip.plasmaAssetId,
  chainId: caip.plasmaChainId,
  name: 'Plasma',
  networkName: 'Plasma',
  symbol: 'XPL',
  precision: 18,
  color: '#8B5CF6',
  networkColor: '#8B5CF6',
  icon: 'https://assets.coingecko.com/coins/images/66489/standard/Plasma-symbol-green-1.png?1755142558',
  networkIcon:
    'https://assets.coingecko.com/asset_platforms/images/32256/small/plasma.jpg?1758000963',
  explorer: 'https://plasmascan.to',
  explorerAddressLink: 'https://plasmascan.to/address/',
  explorerTxLink: 'https://plasmascan.to/tx/',
  relatedAssetKey: null,
})

export const solana: Readonly<Asset> = Object.freeze({
  assetId: caip.solAssetId,
  chainId: caip.solanaChainId,
  name: 'Solana',
  networkName: 'Solana',
  symbol: 'SOL',
  precision: 9,
  color: '#9971d8',
  networkColor: '#9971d8',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/426526def2f327476e868ecb902c515ab17518af/blockchains/solana/info/logo.png',
  explorer: 'https://explorer.solana.com',
  explorerAddressLink: 'https://explorer.solana.com/address/',
  explorerTxLink: 'https://explorer.solana.com/tx/',
  relatedAssetKey: null,
})

export const tron: Readonly<Asset> = Object.freeze({
  assetId: caip.tronAssetId,
  chainId: caip.tronChainId,
  name: 'Tron',
  networkName: 'Tron',
  symbol: 'TRX',
  precision: 6,
  color: '#FF0013',
  networkColor: '#FF0013',
  icon: 'https://rawcdn.githack.com/trustwallet/assets/b7a5f12d893fcf58e0eb1dd64478f076857b720b/blockchains/tron/info/logo.png',
  explorer: 'https://tronscan.org',
  explorerAddressLink: 'https://tronscan.org/#/address/',
  explorerTxLink: 'https://tronscan.org/#/transaction/',
  relatedAssetKey: null,
})

export const sui: Readonly<Asset> = Object.freeze({
  assetId: caip.suiAssetId,
  chainId: caip.suiChainId,
  name: 'Sui',
  networkName: 'Sui',
  symbol: 'SUI',
  precision: 9,
  color: '#6fbcf0',
  networkColor: '#6fbcf0',
  icon: 'https://assets.coingecko.com/coins/images/26375/large/sui_asset.jpeg?1727791290',
  explorer: 'https://suiscan.xyz/mainnet',
  explorerAddressLink: 'https://suiscan.xyz/mainnet/account/',
  explorerTxLink: 'https://suiscan.xyz/mainnet/tx/',
  relatedAssetKey: null,
})

export const near: Readonly<Asset> = Object.freeze({
  assetId: caip.nearAssetId,
  chainId: caip.nearChainId,
  name: 'NEAR Protocol',
  networkName: 'NEAR',
  symbol: 'NEAR',
  precision: 24,
  color: '#00C08B',
  networkColor: '#00C08B',
  icon: 'https://assets.coingecko.com/coins/images/10365/large/near.jpg?1696510367',
  explorer: 'https://nearblocks.io',
  explorerAddressLink: 'https://nearblocks.io/address/',
  explorerTxLink: 'https://nearblocks.io/txns/',
  relatedAssetKey: null,
})

export const starknet: Readonly<Asset> = Object.freeze({
  assetId: caip.starknetAssetId,
  chainId: caip.starknetChainId,
  name: 'Starknet',
  networkName: 'Starknet',
  symbol: 'STRK',
  precision: 18,
  color: '#EC796B',
  networkColor: '#EC796B',
  icon: 'https://assets.coingecko.com/coins/images/26433/large/starknet.png',
  explorer: 'https://starkscan.co',
  explorerAddressLink: 'https://starkscan.co/contract/',
  explorerTxLink: 'https://starkscan.co/tx/',
  relatedAssetKey: null,
})
